import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import * as yup from 'yup';
import { useFormik } from 'formik';
import {
  getOrganizationSettings,
  updateOrganizationSettings,
} from '../../store/actions/organization/CompanyAction';
import Header from '../shared/TailwindComponents/Header';

const CompanySettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const organizationSettings = useSelector((state: RootState) => state.company.settings);

  useEffect(() => {
    dispatch(getOrganizationSettings());
  }, [dispatch]);

  const orgInfoFormik = useFormik({
    initialValues: {
      OrganizationName: '',
      OrganizationNumber: '',
      VAT_Number: '',
      EmailDomain: '',
      ContactEmail: '',
      PhoneNumber: '',
      OfficeAddress: '',
      Website: '',
    },
    validationSchema: yup.object({
      OrganizationName: yup.string().required(t('Organization Name is required')),
      OrganizationNumber: yup.string().required(t('Organization Number is required')),
      ContactEmail: yup.string().email(t('Invalid email')).required(t('Contact Email is required')),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append('OrganizationName', values.OrganizationName);
      formData.append('OrganizationNumber', values.OrganizationNumber);
      formData.append('VAT_Number', values.VAT_Number);
      formData.append('EmailDomain', values.EmailDomain);
      formData.append('ContactEmail', values.ContactEmail);
      formData.append('PhoneNumber', values.PhoneNumber);
      formData.append('OfficeAddress', values.OfficeAddress);
      formData.append('Website', values.Website);
      await dispatch(updateOrganizationSettings(formData));
      dispatch(getOrganizationSettings());
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (organizationSettings) {
      orgInfoFormik.setValues({
        OrganizationName: organizationSettings.OrganizationName || '',
        OrganizationNumber: organizationSettings.OrganizationNumber || '',
        VAT_Number: organizationSettings.VAT_Number || '',
        EmailDomain: organizationSettings.EmailDomain || '',
        ContactEmail: organizationSettings.ContactEmail || '',
        PhoneNumber: organizationSettings.PhoneNumber || '',
        OfficeAddress: organizationSettings.OfficeAddress || '',
        Website: organizationSettings.Website || '',
      });
    }
  }, [organizationSettings]);

  return (
    <div className="">
      <Header />
      <div className="grid w-full min-w-7xl grid-cols-1 gap-4 p-4">
        <div className="px-4 sm:px-6 md:px-0">
          <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
            {t('Organization Information')}
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
            {t("Manage and update your organization's essential details and contact information.")}
          </p>
        </div>
        <div className="md:col-span-2 p-4 bg-white rounded border-1 border border-gray-300 dark:border-gray-700 dark:bg-black">
          <form onSubmit={orgInfoFormik.handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-6">
              <div className="sm:col-span-3">
                <Input
                  label={t('Organization Name')}
                  italicTxt={t('Set the official name of your organization.')}
                  id="OrganizationName"
                  name="OrganizationName"
                  type="text"
                  value={orgInfoFormik.values.OrganizationName}
                  onChange={orgInfoFormik.handleChange}
                  onBlur={orgInfoFormik.handleBlur}
                  error={
                    orgInfoFormik.touched.OrganizationName && Boolean(orgInfoFormik.errors.OrganizationName)
                  }
                  helperText={orgInfoFormik.touched.OrganizationName && orgInfoFormik.errors.OrganizationName}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('Organization Number')}
                  italicTxt={t('Enter your organization number.')}
                  id="OrganizationNumber"
                  name="OrganizationNumber"
                  type="text"
                  value={orgInfoFormik.values.OrganizationNumber}
                  onChange={orgInfoFormik.handleChange}
                  onBlur={orgInfoFormik.handleBlur}
                  error={
                    orgInfoFormik.touched.OrganizationNumber && Boolean(orgInfoFormik.errors.OrganizationNumber)
                  }
                  helperText={orgInfoFormik.touched.OrganizationNumber && orgInfoFormik.errors.OrganizationNumber}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('VAT Number')}
                  italicTxt={t('Enter your VAT number.')}
                  id="VAT_Number"
                  name="VAT_Number"
                  type="text"
                  value={orgInfoFormik.values.VAT_Number}
                  onChange={orgInfoFormik.handleChange}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('Email Domain')}
                  italicTxt={t("Enter your organization's official domain.")}
                  id="EmailDomain"
                  name="EmailDomain"
                  type="text"
                  value={orgInfoFormik.values.EmailDomain}
                  onChange={orgInfoFormik.handleChange}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('Contact Email')}
                  italicTxt={t('Provide the primary email address for communication.')}
                  id="ContactEmail"
                  name="ContactEmail"
                  type="email"
                  value={orgInfoFormik.values.ContactEmail}
                  onChange={orgInfoFormik.handleChange}
                  onBlur={orgInfoFormik.handleBlur}
                  error={
                    orgInfoFormik.touched.ContactEmail && Boolean(orgInfoFormik.errors.ContactEmail)
                  }
                  helperText={
                    orgInfoFormik.touched.ContactEmail && orgInfoFormik.errors.ContactEmail
                  }
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('Phone Number')}
                  italicTxt={t("Enter your organization's contact phone number (optional).")}
                  id="PhoneNumber"
                  name="PhoneNumber"
                  type="text"
                  value={orgInfoFormik.values.PhoneNumber}
                  onChange={orgInfoFormik.handleChange}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('Office Address')}
                  italicTxt={t('Specify the physical address of your office (optional).')}
                  id="OfficeAddress"
                  name="OfficeAddress"
                  type="text"
                  value={orgInfoFormik.values.OfficeAddress}
                  onChange={orgInfoFormik.handleChange}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  label={t('Website')}
                  italicTxt={t("Add your organization's website link (optional).")}
                  id="Website"
                  name="Website"
                  type="text"
                  value={orgInfoFormik.values.Website}
                  onChange={orgInfoFormik.handleChange}
                />
              </div>
            </div>
            <div className="mt-8 flex gap-x-4 justify-end">
              <Button size="small" variant="primary" type="submit" disabled={!orgInfoFormik.dirty}>
                {t('Save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;